import {
  CheckCircle2Icon,
  ExternalLinkIcon,
  GitPullRequestIcon,
  HistoryIcon,
  RefreshCwIcon,
} from "lucide-react";
import type { CmmdTaskHistory, CmmdWorkSession } from "@cmmd-forge/contracts";
import { useId } from "react";

import { Button } from "~/components/ui/button";
import { COARSE_POINTER_HIT_AREA_CLASS, RESPONSIVE_TEXT_BUTTON_CLASS } from "~/lib/touchTarget";
import { formatTaskDateTime } from "./taskDateTime";

export interface ProjectTaskHistoryProps {
  readonly history: CmmdTaskHistory | null;
  readonly loading: boolean;
  readonly error: string | null;
  readonly onLoad: () => void;
  readonly conversationHistory: React.ReactNode;
  readonly workSessions: readonly CmmdWorkSession[];
  readonly workSessionsLoaded: boolean;
  readonly workSessionsLoading: boolean;
  readonly workSessionsError: string | null;
}

export function ProjectTaskHistory(props: ProjectTaskHistoryProps) {
  const headingId = useId();
  if (props.history === null) {
    return (
      <TaskHistoryState
        headingId={headingId}
        loading={props.loading}
        error={props.error}
        onLoad={props.onLoad}
      />
    );
  }
  const { coordinationMessages, repositoryEvidence, pullRequests } = props.history;
  return (
    <section aria-labelledby={headingId} className="scroll-mt-12 space-y-5">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h2
            id={headingId}
            className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
          >
            Work
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Coordination, conversations, verified repository evidence, and pull requests stay
            separate from comments.
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className={RESPONSIVE_TEXT_BUTTON_CLASS}
          disabled={props.loading}
          onClick={props.onLoad}
        >
          <RefreshCwIcon aria-hidden /> Refresh
        </Button>
      </header>
      {props.error !== null ? <HistoryError error={props.error} onRetry={props.onLoad} /> : null}
      <HistorySection heading="Coordination" empty="No authored coordination messages yet.">
        {coordinationMessages.map(({ message, deliveries }) => (
          <li key={message.id} className="rounded-md border border-border bg-card p-3">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">{messageKindLabel(message.kind)}</span>
              <span>{formatTaskDateTime(message.createdAt)}</span>
            </div>
            <p className="mt-2 whitespace-pre-wrap text-sm text-foreground">{message.body}</p>
            <p className="mt-2 text-xs text-muted-foreground">{deliverySummary(deliveries)}</p>
          </li>
        ))}
      </HistorySection>
      <HistorySection heading="Conversations" empty="No linked task conversations yet.">
        {props.conversationHistory}
      </HistorySection>
      <HistorySection heading="Work sessions" empty={workSessionsEmptyMessage(props)}>
        {props.workSessions.map((session) => (
          <li key={session.id} className="rounded-md border border-border bg-card p-3">
            <p className="text-sm font-medium text-foreground">
              {session.runtime.displayName} · {session.status}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {session.threads.length === 0
                ? "No thread is attached to this session."
                : `${session.threads.length} ${session.threads.length === 1 ? "thread" : "threads"} · ${session.threads.map((thread) => thread.title ?? thread.forgeThreadId.slice(0, 8)).join(" · ")}`}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Last activity {formatTaskDateTime(session.lastActivityAt)}
            </p>
          </li>
        ))}
      </HistorySection>
      <HistorySection heading="Repository evidence" empty="No verified repository evidence yet.">
        {repositoryEvidence.map((evidence) => (
          <li
            key={evidence.id}
            className="flex items-start gap-3 rounded-md border border-border bg-card p-3"
          >
            <CheckCircle2Icon
              className="mt-0.5 size-4 shrink-0 text-muted-foreground"
              aria-hidden
            />
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">
                {evidence.name ?? evidence.kind}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">{evidence.summary}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Verified {formatTaskDateTime(evidence.verifiedAt)} ·{" "}
                {evidence.verificationSource.replaceAll("_", " ")}
              </p>
            </div>
          </li>
        ))}
      </HistorySection>
      <HistorySection heading="Pull requests" empty="No pull requests linked to this task yet.">
        {pullRequests.map((pullRequest) => (
          <li key={pullRequest.id} className="rounded-md border border-border bg-card p-3">
            <a
              href={pullRequest.url}
              target="_blank"
              rel="noreferrer"
              className={`${COARSE_POINTER_HIT_AREA_CLASS} inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline`}
            >
              <GitPullRequestIcon className="size-4" aria-hidden />#{pullRequest.number}{" "}
              {pullRequest.title}
              <ExternalLinkIcon className="size-3.5" aria-hidden />
            </a>
            <p className="mt-1 text-xs text-muted-foreground">
              {pullRequest.headRef ?? "Unknown branch"} → {pullRequest.baseRef ?? "Unknown base"} ·
              verified {formatTaskDateTime(pullRequest.lastVerifiedAt)}
            </p>
          </li>
        ))}
      </HistorySection>
    </section>
  );
}

function workSessionsEmptyMessage(
  props: Pick<
    ProjectTaskHistoryProps,
    "workSessionsLoaded" | "workSessionsLoading" | "workSessionsError"
  >,
): string {
  if (props.workSessionsLoading) return "Loading work sessions…";
  if (props.workSessionsError !== null) return props.workSessionsError;
  return props.workSessionsLoaded
    ? "No work sessions are linked to this task yet."
    : "Load Work to inspect sessions.";
}

function TaskHistoryState(props: {
  readonly headingId: string;
  readonly loading: boolean;
  readonly error: string | null;
  readonly onLoad: () => void;
}) {
  if (props.loading) return <p className="text-sm text-muted-foreground">Loading task history…</p>;
  if (props.error !== null) return <HistoryError error={props.error} onRetry={props.onLoad} />;
  return (
    <section aria-labelledby={props.headingId} className="space-y-2">
      <h2
        id={props.headingId}
        className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
      >
        Work
      </h2>
      <p className="text-sm text-muted-foreground">
        Open Work to review coordination, conversations, verified repository evidence, and pull
        requests.
      </p>
      <Button
        type="button"
        size="sm"
        className={RESPONSIVE_TEXT_BUTTON_CLASS}
        onClick={props.onLoad}
      >
        <HistoryIcon aria-hidden /> Load history
      </Button>
    </section>
  );
}

function HistoryError(props: { readonly error: string; readonly onRetry: () => void }) {
  return (
    <div
      role="alert"
      className="flex items-center justify-between gap-3 rounded-md border border-destructive-border bg-destructive-surface px-3 py-2 text-sm text-destructive"
    >
      <span>{props.error}</span>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className={RESPONSIVE_TEXT_BUTTON_CLASS}
        onClick={props.onRetry}
      >
        <RefreshCwIcon aria-hidden /> Retry
      </Button>
    </div>
  );
}

function HistorySection(props: {
  readonly heading: string;
  readonly empty: string;
  readonly children: React.ReactNode;
}) {
  const hasChildren = Array.isArray(props.children)
    ? props.children.length > 0
    : props.children !== null;
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold text-foreground">{props.heading}</h3>
      {hasChildren ? (
        <ol className="space-y-2">{props.children}</ol>
      ) : (
        <p className="text-sm text-muted-foreground">{props.empty}</p>
      )}
    </section>
  );
}

function messageKindLabel(
  kind: CmmdTaskHistory["coordinationMessages"][number]["message"]["kind"],
): string {
  return kind.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function deliverySummary(
  deliveries: CmmdTaskHistory["coordinationMessages"][number]["deliveries"],
): string {
  if (deliveries.length === 0) return "No recipient deliveries recorded.";
  const counts = new Map<string, number>();
  for (const delivery of deliveries)
    counts.set(delivery.status, (counts.get(delivery.status) ?? 0) + 1);
  return [...counts.entries()].map(([status, count]) => `${count} ${status}`).join(" · ");
}
